import { useSubscription } from "@apollo/client";
import { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  AiAssistantChatMessageFragment,
  AiAssistantChatRole,
  AiAssistantSubscriptionVariables,
} from "gql/graphql";
import { AI_ASSISTANT_SUBSCRIPTION } from "@/modules/ai/features/ask-ai-assistant/api/ai-assistant-subscription";

function emptyAnswer(): AiAssistantChatMessageFragment {
  return {
    id: uuidv4(),
    role: AiAssistantChatRole.Assistant,
    text: "",
    toolCalls: [],
  };
}

/**
 * Ведёт один ответ ассистента: этап, инструменты, которые модель дёрнула по
 * дороге, и накопленный текст.
 */
export function useAiAssistantAnswer({
  request,
  onComplete,
  onError,
}: {
  request?: AiAssistantSubscriptionVariables;
  onComplete: (answer: AiAssistantChatMessageFragment) => void;
  /** started — стрим успел начаться: значит отказ не про сам разговор, он живой. */
  onError: (options: { started: boolean }) => void;
}) {
  const [answer, setAnswer] = useState(emptyAnswer);
  const [stage, setStage] = useState<string>();

  const answerRef = useRef(answer);
  const startedRef = useRef(false);

  const reset = () => {
    answerRef.current = emptyAnswer();
    startedRef.current = false;
    setAnswer(answerRef.current);
    setStage(undefined);
  };

  useSubscription(AI_ASSISTANT_SUBSCRIPTION, {
    variables: {
      question: request?.question ?? "",
      chatId: request?.chatId,
    },

    skip: !request,

    onData: ({ data }) => {
      const message = data.data?.aiAssistant;
      startedRef.current = true;

      switch (message?.__typename) {
        case "ChangeStageAiChatMessage":
          setStage(message.stage);
          break;

        case "ToolCallAiChatMessage":
          answerRef.current = {
            ...answerRef.current,
            toolCalls: [...answerRef.current.toolCalls, { tool: message.tool }],
          };

          setAnswer(answerRef.current);
          break;

        case "UpdateMessageAiChatMessage":
          // в text лежит весь ответ на текущий момент, а не очередной кусочек
          answerRef.current = {
            ...answerRef.current,
            text: message.text,
          };

          setAnswer(answerRef.current);
          break;
      }
    },

    onComplete: () => {
      onComplete(answerRef.current);
      reset();
    },

    onError: () => {
      onError({ started: startedRef.current });
      reset();
    },
  });

  return {
    answer,
    stage,
    answering: Boolean(request),
  };
}
